import * as path from 'node:path';
import * as fs from 'node:fs';
import { Command } from 'commander';
import { loadGraph } from '../engine/load.js';
import { displayGraphPath, resolveGraphPath, vibgrateDir } from '../engine/artifacts.js';
import { cacheDir } from '../engine/cache.js';
import { discover } from '../engine/discover.js';
import { probeFreshness, driftCount } from '../engine/freshness.js';
import { c, info, json } from '../util/output.js';
import { applyGlobalOptions, readGlobal, type GlobalOpts } from '../cli-options.js';

/**
 * `vg status` — cache/freshness, counts, staleness, resolver rungs used.
 * Read-only; compares the committed graph against the working tree.
 */
export function registerStatus(program: Command): void {
  const cmd = program
    .command('status')
    .description('cache/freshness, counts, staleness, resolver rungs used')
    .action(async function (this: Command) {
      await runStatus(readGlobal(this));
    });
  applyGlobalOptions(cmd);
}

async function runStatus(global: GlobalOpts): Promise<void> {
  const root = path.resolve(global.cwd ?? '.');
  const graphPath = resolveGraphPath(root, global.graph);
  const graph = loadGraph(root, graphPath);
  const hasCache = fs.existsSync(path.join(cacheDir(root), 'parse-cache.json'));

  // Determine staleness deterministically. Preferred path: the freshness
  // snapshot (stat+hash per corpus file, written by every build) gives an
  // exact per-file answer — content edits, adds, AND removes. Fallback when
  // no snapshot exists on this machine: "files present now" vs "the graph's
  // node file set", which sees adds/removes but not content edits.
  let stale: number | null = null;
  let staleExact = false;
  if (graph) {
    const probe = probeFreshness(root);
    if (probe) {
      stale = driftCount(probe.drift);
      staleExact = true;
    } else {
      const files = discover({ root });
      let changed = 0;
      const graphFiles = new Set(graph.nodes.filter((n) => n.kind === 'file').map((n) => n.file));
      const currentFiles = new Set(files.map((f) => f.rel));
      for (const f of currentFiles) if (!graphFiles.has(f)) changed++;
      for (const f of graphFiles) if (!currentFiles.has(f)) changed++;
      stale = changed;
    }
  }

  if (global.json) {
    json({
      root: path.relative(process.cwd(), root) || '.',
      graphPath: displayGraphPath(root, graphPath),
      built: graph !== null,
      generatedAt: graph?.generatedAt ?? null,
      counts: graph?.meta.counts ?? null,
      languages: graph?.meta.languages ?? [],
      cluster: graph?.meta.cluster ?? null,
      resolver: graph?.provenance.resolver ?? [],
      corpusHash: graph?.provenance.corpusHash ?? null,
      cache: hasCache,
      staleFiles: stale,
      staleExact,
    });
    return;
  }

  if (!graph) {
    info(`${c.cyan('vg')} · no map yet at ${c.dim(displayGraphPath(root, graphPath))}`);
    info(`  run ${c.bold('vg')} to build one`);
    return;
  }

  info(`${c.cyan('vg')} · ${displayGraphPath(root, graphPath)}`);
  info(`  generated ${graph.generatedAt}`);
  info(
    `  nodes ${c.bold(String(graph.meta.counts.nodes))}  edges ${c.bold(
      String(graph.meta.counts.edges),
    )}  areas ${graph.meta.counts.areas}  langs ${graph.meta.languages.join(',') || '—'}`,
  );
  info(`  cluster ${graph.meta.cluster} · resolver ${graph.provenance.resolver.join(',')}`);
  info(`  cache ${hasCache ? c.green('warm') : c.dim('cold')} · corpus ${graph.provenance.corpusHash.slice(0, 12)}…`);
  if (stale && stale > 0) {
    info(c.yellow(`  ${stale} file(s) changed since last build — run ${c.bold('vg')} to refresh`));
  } else {
    info(c.dim('  up to date'));
  }
  if (vibgrateDir(root)) {
    /* artifact dir exists implicitly */
  }
}
